import { InLineAlert, Spinner } from "@cxc/react-fpds-components";
import { UniversalContext, useUniversalContext } from "@cxc/react-universal-components";
import React from "react";
import { useSWRConfig } from "swr";
import { ApiCall, PageCalls } from "../../types/apiTypes";
import { FullConfiguration } from "swr/_internal";

export interface LoadingProviderProps {
  storageKey: string;
  hideDisplay?: boolean;
  children?: React.ReactElement;
}

const LoadingProvider: React.FC<LoadingProviderProps> = ({
                           storageKey,
                           hideDisplay = false,
                           children,
                         }) => {
  const { subscribeScopedValue } = useUniversalContext() as UniversalContext;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  const { cache } = useSWRConfig() as FullConfiguration;

  const pageCalls: PageCalls = subscribeScopedValue("PST", storageKey) as PageCalls;
  if (!pageCalls || pageCalls.length === 0) return <></>;

  const status = determineStatus(pageCalls);

  const isLoading = pageCalls.find(
    (call: ApiCall) => cache.get(call.path)?.isLoading,
  );
  const isError = pageCalls.find(
    (call: ApiCall) => cache.get(call.path)?.error,
  );
  const allDataPresent = pageCalls.find(
    (call: ApiCall) => cache.get(call.path) !== undefined,
  );

  if (
    status === "loading" ||
    isLoading ||
    (!allDataPresent && (!isError || status !== "error"))
  ) {
    if (!hideDisplay) {
      return (
        <Spinner
          size="large"
          style={{
            top: "30%",
            left: "50%",
            position: "fixed",
          }}
          data-testid={"loading-spinner"}
        />
      );
    } else {
      return <></>;
    }
  }

  if (status === "error" || !allDataPresent || isError) {
    if (!hideDisplay) {
      return (
        <InLineAlert
          body={"There was an error loading the data!"}
          variant={"error"}
        />
      );
    } else {
      return <></>;
    }
  }

  return children;
};

const determineStatus = (pageCalls: PageCalls) => {
  const errorCall = pageCalls.find((call) => call.status === "error");
  if (errorCall) return "error";

  const loadingCall = pageCalls.find((call) => call.status === "loading");
  if (loadingCall) return "loading";

  return "success";
};

export default LoadingProvider;
